import querystring from 'querystring';
import { getDefaultCustomerSort } from './search-common';

/**
 * Append a param and paramValue to the query of a given url or path. This method
 * does not check if the param already exists on the provided url
 * @param {string} stringUrlOrPath
 * @param {*} param
 * @param {*} paramValue
 * @returns {string} new string with param appended
 */
export function withQueryParam(stringUrlOrPath, param, paramValue) {
  // if path is missing, don't concatenate anything
  if (stringUrlOrPath == null) {
    return stringUrlOrPath;
  }
  // don't append undefined values
  if (typeof paramValue === 'undefined') {
    return stringUrlOrPath;
  }
  const prefix = stringUrlOrPath.indexOf('?') > -1 ? '&' : '?';
  return `${stringUrlOrPath}${prefix}${param}=${encodeURIComponent(
    paramValue
  )}`;
}

/**
 * Get the base url from the request object for server side requests
 *
 * TODO: Remove this when v8 is retired
 *
 * @param {object} req
 * @returns {string} an empty string for client side requests, and a valid url for SSR
 */
export function getBaseUrlFromRequest(req) {
  if (req == null || req.getAppSettings == null) return '';
  const appSettings = req.getAppSettings();

  return appSettings.PMC_NODE_SERVER_URL;
}

/**
 * Given a location from window, and Login Service URL from app settings
 * build the url to redirect user to login page then back to PMC on successful login
 * @param {object} location
 * @param {string} loginServiceUrl
 * @returns {string}
 */
export function getLoginRedirectUrl(location, loginServiceUrl) {
  const baseUrl = `https://${location.host}${location.pathname}`;
  const params = new URLSearchParams();
  params.append('next', baseUrl);
  const queryString = params.toString();
  return `${loginServiceUrl}/login?${queryString}`;
}

/**
 * Given a customer config, returns the url to reset a search.
 * Customers with "search_home" configured reset with a null search.
 * "search_home" can be "/search" with no filters
 * or include filters example: "/search?filter=provider.accepting_new_patients%3Atrue"
 * @param {object} config
 * @returns {string} url
 */
export function getStartNewSearchUrl(config) {
  const searchHome = config?.search_home;
  if (searchHome) {
    const defaultSort = getDefaultCustomerSort(config);
    let prefix = '?';
    if (searchHome.includes('?')) {
      prefix = '&';
    }
    return `${searchHome}${prefix}sort=${encodeURIComponent(defaultSort)}`;
  }
  return '/';
}

/**
 * Returns the url to find providers at a given marketable location
 * @param {string} locationId
 * @param {object} history
 * @param {string} defaultSort
 * @returns {string} url
 */
export function getProvidersAtLocationUrl(locationId, history, defaultSort) {
  const mLocIdQuery = {
    filter: [
      `locations.associated_marketable_location_ids:${locationId.toUpperCase()}`
    ],
    sort: defaultSort
  };
  const queryString = querystring.stringify(mLocIdQuery);
  return `/search?${queryString}`;
}

/**
 * Get query parameters from a query string
 * @param {string} queryString
 * @returns {Record<string, string | string[]>}
 */
export const getQueryParams = (queryString) => {
  if (!queryString) return {};

  // Remove leading ? if present
  const cleanQueryString = queryString.startsWith('?')
    ? queryString.slice(1)
    : queryString;

  if (!cleanQueryString) return {};

  const params = {};
  const urlParams = new URLSearchParams(cleanQueryString);

  // Convert URLSearchParams to plain object using for...of
  for (const [key, value] of urlParams) {
    if (key in params) {
      // Convert to array on first duplicate, then push subsequent values
      params[key] = Array.isArray(params[key])
        ? [...params[key], value]
        : [params[key], value];
    } else {
      params[key] = value;
    }
  }

  return params;
};

/**
 * Determines if a given URL looks like an internal booking URL.
 * It's extremely unlikely that an external booking link would look like one of ours
 * so we just match on /book/* here
 * @param {string}
 * @returns boolean
 */
export const looksLikeInternalBookingUrl = (url) => {
  return /\/book\/\d+/.test(url);
};
